// react
import { useState, useEffect } from "react";
// kendo
import { process, State } from "@progress/kendo-data-query";
import { Grid, GridPageChangeEvent } from "@progress/kendo-react-grid";
import KendoComboBox from "../../../../common/kendo/KendoComboBox";
import KendoDropDownList from "../../../../common/kendo/KendoDropDownList";
// api
import { GetSubmitPublicNopimsBasketReferenceDataResponseCountrySubDivisionDto } from "../../../../api/Client";
// common
import * as Forms from "../../../../common/forms/BootstrapForms";
import SimpleAlert from "../../../../common/simpleAlerts/SimpleAlertView";
import { IValidationModel } from "../../../../common/validation/ValidationModel";
import ValidationMessageView from "../../../../common/validation/ValidationMessageView";
// me
import { IRootViewModel, ICustomerDetailsViewModel } from "../models/models";
import BasketItemGridColumnsView from "./BasketItemGridColumnView";

interface IProps {
    vm: IRootViewModel;
    validation: IValidationModel;
    onChange: (values: ICustomerDetailsViewModel) => void;
}

export default function SubmitViewDetails(props: IProps): React.JSX.Element {
    const { vm, validation, onChange } = props;

    const [state, setState] = useState<ICustomerDetailsViewModel>(vm.customerDetails);
    const honorifics = vm.referenceData.honorifics;
    const countries = vm.referenceData.countries;
    const subdivisions = vm.referenceData.countrySubdivisions;

    // grid state
    const [pageSize, setPageSize] = useState(10);
    const initialGridState = {
        sort: [{ field: "dataDescription", dir: "asc" }],
        skip: 0,
        take: pageSize,
    } as State;
    const [gridState, setGridState] = useState(initialGridState);

    useEffect(() => {
        setState(vm.customerDetails);
    }, [vm.customerDetails]);

    const [filteredCountrySubdivisions, setFilteredCountrySubdivisions] = useState<GetSubmitPublicNopimsBasketReferenceDataResponseCountrySubDivisionDto[]>([]);
    useEffect(() => {
        setFilteredCountrySubdivisions(state.country ? subdivisions.filter((s) => s.countryId === state.country!.id) : []);
    }, [subdivisions, state.country]);

    function updateViewModel() {
        onChange(state);
    }

    function updateStateAndViewModel(values: ICustomerDetailsViewModel) {
        setState(values);
        onChange(values);
    }

    return (
        <>
            <div className="card mb-2">
                <div className="card-body">
                    <h2 className="card-title">Contact Details</h2>
                    <Forms.Group>
                        <Forms.Row>
                            <Forms.QuarterColumn>
                                <Forms.Label htmlFor="honorificField">Title</Forms.Label>
                                <KendoComboBox
                                    id="honorificField"
                                    data={honorifics}
                                    keyField="id"
                                    textField="name"
                                    value={state.honorific}
                                    onChange={(value) => updateStateAndViewModel({ ...state, honorific: value })}
                                    onBlur={updateViewModel}
                                />
                            </Forms.QuarterColumn>
                            <Forms.QuarterColumn>
                                <Forms.TextInput
                                    id="firstNameField"
                                    label="First Name"
                                    value={state.firstName}
                                    valid={validation.applyValidity("CreateSubmitDetails.FirstName")}
                                    maxLength={100}
                                    onChange={(value) => setState({ ...state, firstName: value })}
                                    onBlur={updateViewModel}
                                />
                                <ValidationMessageView name="CreateSubmitDetails.FirstName" validationModel={validation} />
                            </Forms.QuarterColumn>
                            <Forms.QuarterColumn>
                                <Forms.TextInput
                                    id="lastNameField"
                                    label="Last Name"
                                    value={state.lastName}
                                    valid={validation.applyValidity("CreateSubmitDetails.LastName")}
                                    maxLength={100}
                                    onChange={(value) => setState({ ...state, lastName: value })}
                                    onBlur={updateViewModel}
                                />
                                <ValidationMessageView name="CreateSubmitDetails.LastName" validationModel={validation} />
                            </Forms.QuarterColumn>
                            <Forms.QuarterColumn>
                                <Forms.TextInput
                                    id="emailAddressField"
                                    label="Email"
                                    value={state.emailAddress}
                                    valid={validation.applyValidity("CreateSubmitDetails.EmailAddress")}
                                    maxLength={100}
                                    onChange={(value) => setState({ ...state, emailAddress: value })}
                                    onBlur={updateViewModel}
                                />
                                <ValidationMessageView name="CreateSubmitDetails.EmailAddress" validationModel={validation} />
                            </Forms.QuarterColumn>
                            <Forms.QuarterColumn>
                                <Forms.TextInput
                                    id="contactNumberField"
                                    label="Contact Number"
                                    value={state.contactNumber}
                                    valid={validation.applyValidity("CreateSubmitDetails.ContactNumber")}
                                    maxLength={100}
                                    onChange={(value) => setState({ ...state, contactNumber: value })}
                                    onBlur={updateViewModel}
                                />
                                <ValidationMessageView name="CreateSubmitDetails.ContactNumber" validationModel={validation} />
                            </Forms.QuarterColumn>
                            <Forms.QuarterColumn>
                                <Forms.TextInput
                                    id="addressField"
                                    label="Street Address"
                                    value={state.address}
                                    valid={validation.applyValidity("CreateSubmitDetails.Address")}
                                    maxLength={1000}
                                    onChange={(value) => setState({ ...state, address: value })}
                                    onBlur={updateViewModel}
                                />
                                <ValidationMessageView name="CreateSubmitDetails.Address" validationModel={validation} />
                            </Forms.QuarterColumn>
                            <Forms.QuarterColumn>
                                <Forms.TextInput
                                    id="suburbField"
                                    label="Town/Suburb"
                                    value={state.suburb}
                                    valid={validation.applyValidity("CreateSubmitDetails.Suburb")}
                                    maxLength={100}
                                    onChange={(value) => setState({ ...state, suburb: value })}
                                    onBlur={updateViewModel}
                                />
                                <ValidationMessageView name="CreateSubmitDetails.Suburb" validationModel={validation} />
                            </Forms.QuarterColumn>
                            <Forms.QuarterColumn>
                                <Forms.TextInput
                                    id="postcodeField"
                                    label="Postcode"
                                    value={state.postcode}
                                    valid={validation.applyValidity("CreateSubmitDetails.Postcode")}
                                    maxLength={100}
                                    onChange={(value) => setState({ ...state, postcode: value })}
                                    onBlur={updateViewModel}
                                />
                                <ValidationMessageView name="CreateSubmitDetails.Postcode" validationModel={validation} />
                            </Forms.QuarterColumn>
                            <Forms.QuarterColumn>
                                <Forms.Label htmlFor="countrySubdivisionField">State/Territory</Forms.Label>
                                <KendoDropDownList
                                    id="countrySubdivisionField"
                                    data={filteredCountrySubdivisions}
                                    keyField="id"
                                    textField="name"
                                    value={state.countrySubdivision}
                                    disabled={!state.country}
                                    valid={validation.applyValidity("CreateSubmitDetails.CountrySubdivision")}
                                    onChange={(value) => updateStateAndViewModel({ ...state, countrySubdivision: value })}
                                    onBlur={updateViewModel}
                                />
                                <ValidationMessageView name="CreateSubmitDetails.CountrySubdivision" validationModel={validation} />
                            </Forms.QuarterColumn>
                            <Forms.QuarterColumn>
                                <Forms.Label htmlFor="countryField">Country</Forms.Label>
                                <KendoDropDownList
                                    id="countryField"
                                    data={countries}
                                    keyField="id"
                                    textField="name"
                                    value={state.country}
                                    onChange={(value) => updateStateAndViewModel({ ...state, country: value, countrySubdivision: undefined })}
                                    onBlur={updateViewModel}
                                    valid={validation.applyValidity("CreateSubmitDetails.Country")}
                                />
                                <ValidationMessageView name="CreateSubmitDetails.Country" validationModel={validation} />
                            </Forms.QuarterColumn>
                            <Forms.QuarterColumn>
                                <Forms.TextInput
                                    id="companyNameField"
                                    label="Company"
                                    value={state.companyName}
                                    maxLength={1000}
                                    onChange={(value) => setState({ ...state, companyName: value })}
                                    onBlur={updateViewModel}
                                />
                            </Forms.QuarterColumn>
                        </Forms.Row>
                    </Forms.Group>
                    <Forms.Group>
                        <Forms.Row>
                            <Forms.FullColumn>
                                <Forms.TextArea
                                    id="commentsField"
                                    label="Comments"
                                    value={state.comments}
                                    valid={validation.applyValidity("CreateSubmitDetails.Comments")}
                                    maxLength={1000}
                                    onChange={(value) => setState({ ...state, comments: value })}
                                    onBlur={updateViewModel}
                                    rows={3}
                                />
                                <ValidationMessageView name="CreateSubmitDetails.Comments" validationModel={validation} />
                            </Forms.FullColumn>
                        </Forms.Row>
                    </Forms.Group>
                </div>
            </div>
            {vm.requestedBasketItems.filter((i) => i.downloadUrl).length > 0 && (
                <SimpleAlert message="There are basket items with a downloadable link, if they are included in the Request they will incur a cost." alertType="warning" dismissable={false} />
            )}
            <Grid
                pageable={{ pageSizes: [10, 20, 50, 100], pageSizeValue: pageSize, responsive: false }}
                onPageChange={(e: GridPageChangeEvent) => {
                    setPageSize(e.page.take);
                    setGridState({ ...gridState, take: e.page.take, skip: e.page.skip });
                }}
                sortable
                resizable
                navigatable
                data={process(vm.requestedBasketItems, gridState)}
                {...gridState}
                onDataStateChange={(e) => {
                    setGridState(e.dataState);
                }}
            >
                {BasketItemGridColumnsView()}
            </Grid>
        </>
    );
}
